'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { calculateReadingTime } from '../../utils/calculate-reading-time';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { Paragraph } from '../ui/paragraph';

export type BlogCardProps = {
  isFirst?: boolean;
  blog: Blog;
};

export function BlogCard({ blog, isFirst = false }: BlogCardProps) {
  const textContent = getTextFromHTML(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  if (isFirst)
    return (
      <Link
        prefetch={false}
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        className="relative col-span-1 flex h-[450px] w-full flex-col items-end justify-end overflow-hidden rounded-[12px] p-4 sm:h-[520px] sm:p-5 md:h-[540px] lg:h-[647px] lg:p-6"
      >
        <div className="relative z-10 flex w-full items-end justify-between">
          <div className="flex w-full max-w-[656px] flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6 xl:gap-8">
            <div className="flex w-full flex-col items-center justify-start gap-3 sm:gap-4">
              <div className="flex w-full flex-wrap items-center justify-start gap-2">
                {blog.categories?.map(category => (
                  <div
                    key={category.name}
                    className="flex h-[21px] items-center justify-center whitespace-nowrap rounded-full bg-white px-2 text-xs font-semibold tracking-tight sm:h-[23px] sm:px-3 sm:text-sm"
                  >
                    {category.name}
                  </div>
                ))}
                <Paragraph color="white" className="h6 ml-2 font-semibold">
                  {formattedDate}&nbsp;&nbsp;<span className="text-border">|</span>&nbsp;&nbsp;
                  {calculateReadingTime(textContent)} min. čítanie
                </Paragraph>
              </div>
              <Heading
                title={blog.name}
                className={{
                  title: 'text-white',
                  container: 'w-full items-start justify-start text-left',
                }}
              />
            </div>
            {/* The whole card is a link; a <button> inside <a> is invalid and double-announced. */}
            <Button asChild variant={'secondary'} className="w-fit">
              <span>Prečítať článok</span>
            </Button>
          </div>
        </div>
        <div className="ea-blog-card__first-overlay absolute inset-0 z-[1] h-full w-full" />
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </Link>
    );

  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="col-span-1 flex w-full flex-col items-start justify-start gap-4 md:flex-row md:items-center lg:gap-6"
    >
      <div className="relative h-[210px] w-full overflow-hidden rounded-[12px] md:max-w-[332px] lg:h-[190px]">
        <div className="absolute left-3 top-3 z-10 flex flex-wrap items-start justify-start gap-2 pr-3">
          {blog.categories?.map(category => (
            <div
              key={category.name}
              className="flex h-[21px] items-center justify-center whitespace-nowrap rounded-full bg-white px-2 text-xs font-semibold tracking-tight sm:h-[23px] sm:px-3 sm:text-sm"
            >
              {category.name}
            </div>
          ))}
        </div>
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="( min-width: 640px ) 332px, 100vw"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3 lg:gap-4">
        <p className="h6 font-semibold tracking-tight text-[#666666]">
          {formattedDate}&nbsp;&nbsp;<span className="text-border">|</span>&nbsp;&nbsp;
          {calculateReadingTime(textContent)} min. čítanie
        </p>
        <Heading tag="h2" styleAs="h5" title={blog.name} className={{ title: 'line-clamp-2' }} />
        <Button asChild variant={'secondary'}>
          <span>Prečítať článok</span>
        </Button>
      </div>
    </Link>
  );
}
